import { useEffect, useRef } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { Box, type SxProps, type Theme } from '@mui/material';
import { notchedOutlineColor } from '@core/components';
import type { FabricFlag } from '../../utils/fabric';
import { OptionalPresenceField } from './OptionalPresenceField';
import { RhfColorSelect } from './RhfColorSelect';
import { RhfFabricSelect } from './RhfFabricSelect';

/** Fabric and color share the pane 45:30, so the row is 25% / 45% / 30%. */
const FABRIC_FLEX = 45;
const COLOR_FLEX = 30;

const paneSx = (flexGrow: number): SxProps<Theme> => ({
  flex: `${flexGrow} 1 0`,
  minWidth: 0,
  height: '100%',
  overflow: 'hidden',
  '&:hover': { bgcolor: 'action.hover' },
});

const SELECT_SX: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  '& .MuiInputLabel-root': { display: 'none' },
  '& .MuiFormHelperText-root': { display: 'none' },
  '& .MuiAutocomplete-root': { height: '100%' },
  '& .MuiAutocomplete-inputRoot': {
    flexWrap: 'nowrap',
    overflow: 'hidden',
  },
  '& .MuiOutlinedInput-root': {
    height: '100%',
    borderRadius: 0,
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' },
  },
  // Autocomplete sizes the input from the value length; force it to the pane
  // and pin overflow so long labels ellipsis from the start (not mid-word).
  '& .MuiAutocomplete-input': {
    width: '100% !important',
    minWidth: '0 !important',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

type OptionalFabricColorFieldProps<T extends FieldValues> = {
  label: string;
  withName: FieldPath<T>;
  fabricName: FieldPath<T>;
  colorName: FieldPath<T>;
  fabricFlag?: FabricFlag;
};

/**
 * Optional fabric+color presence: Without by default; With expands fabric and
 * color selects and focuses fabric for immediate choice.
 */
export function OptionalFabricColorField<T extends FieldValues>({
  label,
  withName,
  fabricName,
  colorName,
  fabricFlag,
}: OptionalFabricColorFieldProps<T>) {
  const { control, setValue } = useFormContext<T>();
  const { field: withField } = useController({ control, name: withName });
  const enabled = Boolean(withField.value);
  const fabricValue = useWatch({ control, name: fabricName });
  const fabricInputRef = useRef<HTMLInputElement>(null);
  const wasEnabledRef = useRef(enabled);

  useEffect(() => {
    const wasEnabled = wasEnabledRef.current;
    wasEnabledRef.current = enabled;
    if (!wasEnabled && enabled) {
      const id = window.setTimeout(() => {
        const input = fabricInputRef.current;
        input?.focus();
        if (input) {
          input.scrollLeft = 0;
        }
      }, 300);
      return () => window.clearTimeout(id);
    }
  }, [enabled]);

  // Autocomplete scrolls the input to the end when a long value is set / focused.
  useEffect(() => {
    const input = fabricInputRef.current;
    if (!input) {
      return;
    }
    const pinStart = () => {
      input.scrollLeft = 0;
    };
    pinStart();
    input.addEventListener('blur', pinStart);
    return () => input.removeEventListener('blur', pinStart);
  }, [fabricValue, enabled]);

  return (
    <OptionalPresenceField
      presenceLabel={label}
      enabled={enabled}
      onEnabledChange={(next) => {
        withField.onChange(next);
        if (!next) {
          setValue(fabricName, '' as never, { shouldDirty: true });
          setValue(colorName, '' as never, { shouldDirty: true });
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          width: '100%',
          height: '100%',
          minWidth: 0,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `calc(100% * ${FABRIC_FLEX} / ${FABRIC_FLEX + COLOR_FLEX})`,
            borderLeft: '1px solid',
            borderColor: notchedOutlineColor,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        <Box sx={paneSx(FABRIC_FLEX)}>
          <RhfFabricSelect<T>
            name={fabricName}
            label=""
            flag={fabricFlag}
            openOnFocus
            disableClearable
            inputRef={fabricInputRef}
            sx={SELECT_SX}
          />
        </Box>
        <Box sx={paneSx(COLOR_FLEX)}>
          <RhfColorSelect<T>
            name={colorName}
            label=""
            fabricFieldName={fabricName}
            disableClearable
            sx={SELECT_SX}
          />
        </Box>
      </Box>
    </OptionalPresenceField>
  );
}
